import { useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { useAppSelector, useAppDispatch } from "../../state/store";
import { Grid } from "@mui/material";
import { ChapterSelector, SettingsSelector } from "../../components/settings";
import { InfoBox } from "./InfoBox";
import { ESVText } from "../../components/ESVText";
import { GameSettings } from "../../components/settings/GameSettings";
import { Row } from "../../components/Row";
import { Check } from "../../components/Check/Check";
import { TournamentLink } from "../../components/TournamentComponents/TournamentLink";
import { chapterSelected } from "../../util/generalUtil";
import {
  SET_QUIZ_NOW_QUIZZERS,
  SET_SETTINGS,
} from "../../state/reducers/settings";
import { updateUser } from "../../state/actions/authentication";
import { ReactComponent as DarkMode } from "../../icons/dark-mode-icon.svg";
import { ReactComponent as LightMode } from "../../icons/light-mode-icon.svg";
import { ReactComponent as QuizNowIcon } from "../../icons/mmButtonIcons/question-answer.svg";
import { ReactComponent as PracticeIcon } from "../../icons/mmButtonIcons/bars-up.svg";
import { ReactComponent as GamesIcon } from "../../icons/mmButtonIcons/star-medal.svg";
import { ReactComponent as MultiplayerIcon } from "../../icons/mmButtonIcons/people-community.svg";
import { ReactComponent as TournamentsIcon } from "../../icons/mmButtonIcons/trophy.svg";
import { ReactComponent as ScorekeeperIcon } from "../../icons/mmButtonIcons/score-sheets.svg";
import { ReactComponent as SettingsIcon } from "../../icons/mmButtonIcons/settings.svg";
import { ReactComponent as ChaptersIcon } from "../../icons/mmButtonIcons/check.svg";
import { ReactComponent as VerseCardsIcon } from "../../icons/mmButtonIcons/cards.svg";
import { ReactComponent as MemorizeIcon } from "../../icons/mmButtonIcons/head-idea.svg";
import { ReactComponent as QuestionDrillIcon } from "../../icons/mmButtonIcons/question-drill.svg";
import { ReactComponent as ReactionTestIcon } from "../../icons/mmButtonIcons/stopwatch.svg";
import { ReactComponent as BibleGameIcon } from "../../icons/mmButtonIcons/bible-game.svg";
import { ReactComponent as ConcentrationIcon } from "../../icons/mmButtonIcons/concentration.svg";
import { ReactComponent as VerseDetectiveIcon } from "../../icons/mmButtonIcons/verse-detective.svg";
import { ReactComponent as EpicBattleIcon } from "../../icons/mmButtonIcons/swords.svg";
import "./MainMenu.scss";

export function MainMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.settings);
  const ModeIcon = settings.lightMode ? LightMode : DarkMode;
  const { selectedSeason, selectedEvent } = useAppSelector((state) => state.sk);
  const appDetails = useAppSelector((state) => state.appDetails);
  const { user } = useAppSelector((state) => state.authentication);

  const isChapterSelected = useMemo(
    () => chapterSelected(settings.selectedChapters),
    [settings.selectedChapters]
  );
  const view: string = location?.state?.menuView || "Menu";
  const setView = (view: string) => {
    navigate(location.pathname, { state: { menuView: view } });
  };

  let mainContent = null;

  switch (view) {
    case "Chapters":
      mainContent = (
        <ChapterSelector
          onClickBack={(newSettings) => {
            if (user) {
              dispatch(
                updateUser({
                  ...user,
                  preferences: {
                    ...user.preferences,
                    settings: {
                      ...newSettings,
                      // @ts-ignore
                      selectedChapters: undefined,
                      touchscreen: undefined,
                    },
                  },
                })
              );
            }
          }}
        />
      );
      break;
    case "Settings":
      mainContent = (
        <SettingsSelector
          onClickBack={(newSettings) => {
            if (user) {
              dispatch(
                updateUser({
                  ...user,
                  preferences: {
                    ...user.preferences,
                    settings: {
                      ...newSettings,
                      // @ts-ignore
                      selectedChapters: undefined,
                      touchscreen: undefined,
                    },
                  },
                })
              );
            }
          }}
        />
      );
      break;
    case "QuizNow":
      mainContent = (
        <GameSettings
          title="Quiz Now"
          description="Enter the names of everyone who is quizzing."
          onStart={(players) => {
            dispatch({
              type: SET_QUIZ_NOW_QUIZZERS,
              payload: players.map((p) => p.name),
            });
            window.scrollTo(0, 0);
          }}
          gamePage="/quiznow"
        />
      );
      break;
    case "Concentration":
      mainContent = (
        <GameSettings
          title="Concentration"
          description="Concentration (or Memory), but you're matching cards that have the reference with cards that have the corresponding verse. Whoever finds the most matches wins!"
          minQuizzers={2}
          gamePage="/concentration"
          settingsKey="concentration"
          extraSettings={{
            defaultSettings: {
              verses: 15,
            },
            settingsUI: (settings, setSettings) => (
              <div>
                <p>Number of Verses:</p>
                <Row>
                  {[10, 15, 20, 30].map((num) => (
                    <Check
                      key={num}
                      checked={settings.verses === num}
                      onClick={() => setSettings({ ...settings, verses: num })}
                      style={{ width: 50, marginRight: 5 }}
                    >
                      {String(num)}
                    </Check>
                  ))}
                </Row>
              </div>
            ),
          }}
        />
      );
      break;
    case "VerseDetective":
      mainContent = (
        <GameSettings
          title="Verse Detective"
          description="A random first word of a verse is given. Players take turns giving the next word in a verse, trying to continue the verse using one of the multiple possible verses. If you give a word that does not match any of the possible verses, you lose a life. If your word finishes a verse, a new first word is generated for the next player. The turn order will be rearranged slightly with each new verse. If you lose all three lives, you're out."
          minQuizzers={2}
          extraSettings={{
            defaultSettings: {
              mode: "ALL" as "SELECTED" | "ALL",
            },
            settingsUI: (settings, setSettings) => (
              <>
                <p style={{ fontWeight: "bold", marginTop: 20 }}>
                  Choose a Material Option:
                </p>
                <Grid container spacing={1} style={{ marginTop: 2 }}>
                  <Grid size={6}>
                    <Check
                      checked={settings.mode === "SELECTED"}
                      onClick={() => setSettings({ mode: "SELECTED" })}
                    >
                      Selected Material
                    </Check>
                    <p>
                      Limit the allowed verses to only the selected material.
                    </p>
                  </Grid>
                  <Grid size={6}>
                    <Check
                      checked={settings.mode === "ALL"}
                      onClick={() => setSettings({ mode: "ALL" })}
                    >
                      Full New Testament
                    </Check>
                    <p>Play over all verses in the New Testament.</p>
                  </Grid>
                </Grid>
              </>
            ),
          }}
          gamePage="/versedetective"
          settingsKey="verseDetective"
        />
      );
      break;
    case "EpicBattle":
      mainContent = (
        <GameSettings
          title="Epic Battle"
          description="Each player gets asked a verse from their material. If you get your verse wrong, you lose a life. If all players get their verse right, the player who took the most time loses a life. Once you lose all three lives, you're out. Last player standing wins!"
          minQuizzers={2}
          hasMaterial
          onStart={(players) => {
            navigate("/epicbattle", { state: { epicBattle: { players } } });
          }}
          gamePage="/epicbattle"
          settingsKey="epicBattle"
        />
      );
      break;
    case "BibleGame":
      mainContent = (
        <GameSettings
          title="The Bible Game"
          description="Reverse flash cards! Each player gets a verse and must guess the reference. How many points can you score in nine rounds?"
          hasMaterial={(settings) => settings.mode === "SELECTED"}
          extraSettings={{
            defaultSettings: {
              mode: "SELECTED" as "SELECTED" | "ALL",
            },
            settingsUI: (settings, setSettings) => (
              <>
                <p style={{ fontWeight: "bold", marginTop: 20 }}>
                  Choose a Game Mode:
                </p>
                <Grid container spacing={1} style={{ marginTop: 2 }}>
                  <Grid size={6}>
                    <Check
                      checked={settings.mode === "SELECTED"}
                      onClick={() => setSettings({ mode: "SELECTED" })}
                    >
                      Selected Material
                    </Check>
                    <p>
                      Select material for each player, and get one point only if
                      you get the full reference correct.
                    </p>
                  </Grid>
                  <Grid size={6}>
                    <Check
                      checked={settings.mode === "ALL"}
                      onClick={() => setSettings({ mode: "ALL" })}
                    >
                      Full New Testament
                    </Check>
                    <p>
                      Get asked any verse from the New Testament, and you score
                      one point for getting the book, chapter, or verse correct,
                      for up to three points per round.
                    </p>
                  </Grid>
                </Grid>
              </>
            ),
          }}
          minQuizzers={1}
          gamePage="/biblegame"
          settingsKey="bibleGame"
        />
      );
      break;
    case "Contact":
      mainContent = (
        <InfoBox onGoBack={() => setView("Menu")}>
          <>
            <p>
              <span>{"Copyright © 2025 "}</span>
              <TournamentLink pageId="Peter Gossell" />
              <span>{`.${
                appDetails.version ? ` Version ${appDetails.version}` : ""
              }`}</span>
            </p>
            <p>Latest Question Set Version: September 2026</p>
            <p>
              Email:{" "}
              <a href="mailto:peter@biblequizacademy.com">
                peter@biblequizacademy.com
              </a>
            </p>
            <p>
              2016/2025 version of the English Standard Version (ESV) ©{" "}
              <a href="https://www.crossway.org">Crossway</a>.{" "}
              <label className="link" onClick={() => setView("ESV")}>
                Learn More
              </label>
            </p>
            <p>
              For more information about Bible Quizzing and BQF, see{" "}
              <a href="http://www.summitbiblequizzing.org">
                Summit Bible Quizzing
              </a>{" "}
              and{" "}
              <a href="http://www.biblequizfellowship.org">
                Bible Quiz Fellowship
              </a>
              .
            </p>
          </>
        </InfoBox>
      );
      break;
    case "Contribute":
      mainContent = (
        <InfoBox onGoBack={() => setView("Menu")}>
          <>
            <h3>Code with us!</h3>
            <p>
              If you want to help program new features to BQA, or are even
              remotely interested in it, please do get in touch. We use{" "}
              <a href="https://reactjs.org/">ReactJS</a>,{" "}
              <a href="https://expressjs.com/">ExpressJS</a>,{" "}
              <a href="https://www.typescriptlang.org/">TypeScript</a>, and{" "}
              <a href="https://git-scm.com/">Git</a>, though you do not need
              experience in those technologies to get involved. All I ask is
              that you are enthusiastic about Bible Quizzing and have some
              programming experience.&nbsp;
              <button
                className="link"
                style={{ padding: 0 }}
                onClick={() => setView("Contact")}
              >
                Email me
              </button>{" "}
              for details. Perks include being a part of one of the most
              up-to-date pieces of quizzing software, a great item for your
              résumé, and even your own biblequizacademy.com email address!
            </p>
            <h3>Tournaments</h3>
            <p>
              Our <Link to="/tournaments">Tournament Tracker</Link> needs a lot
              of data from a lot of different sources, including rosters, stats,
              quizzer bios, and more. If you are interested in keeping
              tournaments from your ministry up-to-date, or filling in data from
              past tournaments,{" "}
              <button
                className="link"
                style={{ padding: 0 }}
                onClick={() => setView("Contact")}
              >
                let's get in touch
              </button>
              .
            </p>
            <h3>Beta Testers</h3>
            <p>
              I also need volunteers to beta test new features and give feedback
              to me, or whoever else is working on them. Again,&nbsp;
              <button
                className="link"
                style={{ padding: 0 }}
                onClick={() => setView("Contact")}
              >
                Email me
              </button>{" "}
              for details. Perks include being the first to see new features and
              being able to provide direct feedback which will influence how BQA
              looks in the future.
            </p>
            <p style={{ marginTop: 10 }}>
              If you would like to contribute financially to the advancement of
              Bible Quizzing and Bible Quiz Academy, contribute instead to your
              local quizzers who would otherwise be unable or unwilling to go to
              Nationals.
            </p>
          </>
        </InfoBox>
      );
      break;
    case "Help":
      mainContent = (
        <InfoBox onGoBack={() => setView("Menu")}>
          <>
            <p>
              <b>To get started</b>, visit the Chapters menu and select your
              material. You can quiz over any material in the New Testament.
              Check out the Settings menu to see what options are available to
              you for the various quiz modes.
            </p>
            <p>
              <b>Quiz Now</b> is just like individual quizzing, just on your
              device. Type in the names of all quizzers, click Start, and you
              will be in a quiz. In the Settings menu, you can decide how you
              want to jump: using buttons on the screen (best for mobile
              devices) or with keys on the keyboard (best for non-touchscreen
              devices). Once in the game, click Ask Question, and words will
              appear on the screen. Jump when you're ready, and you'll be all
              set!
            </p>
            <p>
              <b>Verse Cards</b> allows you to review your verses in order or
              randomized. Just click Next Verse and quote your verse. You can
              also use Speech to Text, where you say the verse out loud and your
              microphone will put your words on the screen. Or if you have a
              keyboard, you can type the first letter in each word to get
              checked against the verse. You still make the final judgement of
              whether you got it right or wrong, but you can use the computer to
              help you out.
            </p>
            <p>
              Several other tools for quizzers include <b>Memorize</b>, a tool
              like Verse Cards to help you memorize new verses, and also some
              advanced features like <b>Question Drill</b> to work on figuring
              out all the questions in the verse and <b>Reaction Test</b> to
              test your reaction speed with quiz pads.
            </p>
            <p>
              <b>Multiplayer Quizzing</b> allows you to quiz with other quizzers
              on different devices. No need for Zoom, no worry about lag - you
              don't even need a quizmaster - everything is automatically handled
              by BQA! Microphone access will be required. See for yourself by
              clicking Multiplayer from the main menu. In multiplayer Quizzing,
              you can quiz individually or <b>on teams</b>.
            </p>
            <p>
              <b>Scorekeeper</b> in BQA allows coaches to easily keep score for
              their team. See{" "}
              <button
                className="link"
                onClick={() => navigate("/sk/help")}
                style={{ fontSize: 16, marginLeft: -4 }}
              >
                Scorekeeper Help
              </button>
              for more information.
            </p>
          </>
        </InfoBox>
      );
      break;
    case "ESV":
      mainContent = (
        <InfoBox onGoBack={() => setView("Menu")}>
          <>
            <ESVText />
            <p>
              See{" "}
              <a
                href="https://www.esv.org/resources/esv-global-study-bible/copyright-page/"
                target="_blank"
                rel="noopener noreferrer"
              >
                ESV's Website
              </a>{" "}
              for information on how you can freely use their text.
            </p>
          </>
        </InfoBox>
      );
      break;
    case "Games":
      mainContent = (
        <div className="main-view-container">
          <h2>Quizzing Games</h2>
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            style={{ marginBottom: 30 }}
          >
            <Grid size={6}>
              <Link
                className="large-button"
                key="con-btn"
                to="#"
                state={{
                  menuView: isChapterSelected ? "Concentration" : "Chapters",
                }}
                replace={false}
              >
                <ConcentrationIcon />
                Concentration
              </Link>
            </Grid>
            <Grid size={6}>
              <Link
                className="large-button"
                to="#"
                state={{ menuView: "EpicBattle" }}
                replace={false}
                key="epic-battle-btn"
              >
                <EpicBattleIcon />
                Epic Battle
              </Link>
            </Grid>
            <Grid size={6}>
              <Link
                className="large-button"
                to="#"
                state={{
                  menuView: "BibleGame",
                }}
                replace={false}
                key="vc-btn"
              >
                <BibleGameIcon />
                The Bible Game
              </Link>
            </Grid>
            <Grid size={6}>
              <Link
                className="large-button"
                to="#"
                state={{
                  menuView: "VerseDetective",
                }}
                replace={false}
                key="vc-btn"
              >
                <VerseDetectiveIcon />
                Verse Detective
              </Link>
            </Grid>
          </Grid>
          <Link
            className="secondary-btn"
            to={location.pathname}
            replace={false}
          >
            Go Back
          </Link>
        </div>
      );
      break;
    case "Practice":
      mainContent = (
        <div className="main-view-container">
          <h2>Practice Tools & Drills</h2>
          {!isChapterSelected && (
            <div className="important-information" style={{ marginBottom: 5 }}>
              Please select a chapter
            </div>
          )}
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            style={{ marginBottom: 30 }}
          >
            <Grid size={6}>
              {isChapterSelected ? (
                <Link to="/versecards" className="large-button" key="vc-link">
                  <VerseCardsIcon />
                  Verse Cards
                </Link>
              ) : (
                <button
                  className="large-button"
                  onClick={() => {
                    setView("Chapters");
                  }}
                  key="vc-btn"
                >
                  <VerseCardsIcon />
                  Verse Cards
                </button>
              )}
            </Grid>
            <Grid size={6}>
              {isChapterSelected ? (
                <Link
                  to="/memorize"
                  className="large-button"
                  onClick={() => window.scrollTo(0, 0)}
                  key="mem-link"
                >
                  <MemorizeIcon />
                  Memorize
                </Link>
              ) : (
                <button
                  className="large-button"
                  onClick={() => {
                    setView("Chapters");
                  }}
                  key="vc-btn"
                >
                  <MemorizeIcon />
                  Memorize
                </button>
              )}
            </Grid>
            <Grid size={6}>
              {isChapterSelected ? (
                <Link
                  to="/questiondrill"
                  className="large-button"
                  onClick={() => window.scrollTo(0, 0)}
                  key="qd-link"
                >
                  <QuestionDrillIcon />
                  Question Drill
                </Link>
              ) : (
                <button
                  className="large-button"
                  onClick={() => {
                    if (!isChapterSelected) {
                      setView("Chapters");
                      return;
                    }
                    navigate("/questiondrill");
                    window.scrollTo(0, 0);
                  }}
                  key="qd-btn"
                >
                  <QuestionDrillIcon />
                  Question Drill
                </button>
              )}
            </Grid>
            <Grid size={6}>
              <Link
                to="/reaction"
                className="large-button"
                onClick={() => window.scrollTo(0, 0)}
                key="reaction"
              >
                <ReactionTestIcon />
                Reaction Test
              </Link>
            </Grid>
          </Grid>
          <Link
            className="secondary-btn"
            to={location.pathname}
            replace={false}
          >
            Go Back
          </Link>
        </div>
      );
      break;
    default:
      mainContent = (
        <>
          <div className="main-view-container" style={{ marginTop: 40 }}>
            {!isChapterSelected && (
              <div
                className="important-information"
                style={{ marginBottom: 5 }}
              >
                Quizzers: Please select a chapter
              </div>
            )}
            <Grid
              container
              spacing={{ xs: 1, sm: 2 }}
              style={{ marginBottom: 32 }}
            >
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={location.pathname}
                  replace={false}
                  state={{
                    menuView: isChapterSelected ? "QuizNow" : "Chapters",
                  }}
                  key="qn"
                >
                  <QuizNowIcon />
                  Quiz Now
                </Link>
              </Grid>
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={location.pathname}
                  replace={false}
                  state={{
                    menuView: "Practice",
                  }}
                  key="practice"
                >
                  <PracticeIcon style={{ width: 28, height: 28 }} />
                  Practice
                </Link>
              </Grid>
              <Grid size={6}>
                <Link className="large-button" to="/multiplayer">
                  <MultiplayerIcon />
                  Multiplayer
                </Link>
              </Grid>
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={location.pathname}
                  replace={false}
                  state={{
                    menuView: "Games",
                  }}
                  key="games"
                >
                  <GamesIcon />
                  Games
                </Link>
              </Grid>
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={
                    selectedSeason
                      ? `/sk/${selectedSeason.seasonId}/${
                          selectedEvent || "events"
                        }`
                      : "/sk/seasons"
                  }
                >
                  <ScorekeeperIcon />
                  Scorekeeper
                </Link>
              </Grid>
              <Grid size={6}>
                <Link className="large-button" to="/tournaments">
                  <TournamentsIcon />
                  Tournaments
                </Link>
              </Grid>
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={location.pathname}
                  replace={false}
                  state={{ menuView: "Chapters" }}
                >
                  <ChaptersIcon />
                  Chapters
                </Link>
              </Grid>
              <Grid size={6}>
                <Link
                  className="large-button"
                  to={location.pathname}
                  replace={false}
                  state={{ menuView: "Settings" }}
                >
                  <SettingsIcon />
                  Settings
                </Link>
              </Grid>
            </Grid>
          </div>
          <div className="menu-footer">
            <div
              style={{
                display: "flex",
                position: "absolute",
                right: 15,
                paddingTop: 14,
              }}
            >
              <button
                style={{
                  color: settings.lightMode ? "#111111" : "#cccccc",
                  marginRight: 8,
                  background: "none",
                }}
                onClick={() => {
                  dispatch({
                    type: SET_SETTINGS,
                    payload: {
                      ...settings,
                      lightMode: !settings.lightMode,
                    },
                  });
                  if (user) {
                    dispatch(
                      updateUser({
                        ...user,
                        preferences: {
                          ...user.preferences,
                          settings: {
                            ...settings,
                            lightMode: !settings.lightMode,
                          },
                        },
                      })
                    );
                  }
                }}
                aria-label={`Switch to ${
                  settings.lightMode ? "dark" : "light"
                } mode`}
              >
                <ModeIcon height={18} width={18} />
              </button>
              <button
                className="link"
                style={{ marginRight: 8 }}
                onClick={() => setView("ESV")}
              >
                ESV
              </button>
              <button
                className="link"
                style={{ marginRight: 8 }}
                onClick={() => setView("Help")}
              >
                Help
              </button>
              <button
                className="link"
                style={{ marginRight: 8 }}
                onClick={() => setView("Contact")}
              >
                About
              </button>
              <button
                className="link"
                style={{ marginRight: 8 }}
                onClick={() => setView("Contribute")}
              >
                Get Involved
              </button>
            </div>
          </div>
          <div
            style={{
              position: "absolute",
              right: 0,
              top: 0,
            }}
          >
            {user?.securityClearance?.logonType === "ADMIN" && (
              <a href="/admin" style={{ marginRight: 10 }}>
                Admin
              </a>
            )}
            {user ? (
              <Link
                className="link"
                to="/user"
                style={{ marginRight: 10 }}
                aria-label={`User Details for ${user.userName}`}
              >{`Hi, ${user.userName}`}</Link>
            ) : (
              <button
                style={{ marginRight: 10 }}
                onClick={() => navigate("/login")}
                className="link"
              >
                Login
              </button>
            )}
          </div>
        </>
      );
  }
  return (
    <div className="page" style={{ minWidth: "unset", padding: "unset" }}>
      <h1
        style={{
          margin: 0,
          color: "unset",
          textAlign: "center",
          paddingTop: 15,
          fontSize: "min(56px, max(5.5vw, 24px))",
        }}
        aria-label="Bible Quiz Academy - Main Menu"
      >
        <span>Bible Quiz Academy</span>
        <span style={{ fontSize: 16 }}>.com</span>
      </h1>
      <p style={{ position: "fixed", top: -1000, left: -1000 }}>
        Tools for quizzers and coaches in Bible Quiz Fellowship. Review your
        verses, memorize more material, and keep scores and statistics with
        Bible Quiz Academy.
      </p>
      <hr
        style={{
          backgroundColor: "grey",
          height: 2,
          marginTop: 15,
          border: "0",
          marginLeft: -8,
        }}
      />
      <div
        style={{
          marginTop: 0,
          marginBottom: 30,
          position: "relative",
          display: "flex",
          padding: "0px 20px",
        }}
      >
        {mainContent}
      </div>
    </div>
  );
}
